import logging from '@sstdev/lib_logging';
import { contexts, hooks, Modal, testProperties, Text, useDebounce } from 'lib_ui-primitives';
import { constants, continuousScanBridge, getContinuousScanTransport, metadata } from 'lib_ui-services';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {
    Fragment,
    memo,
    createElement as rc,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useTheme } from 'styled-components';
import useBlockReaderReadsOnSetupScreen from '../../../hooks/useBlockReaderReadsOnSetupScreen';
import useContinuousScanEnabled from '../../../hooks/useContinuousScanEnabled';
import useDbView from '../../../hooks/useDbView';
import useEventSink from '../../../hooks/useEventSink';
import useNavigationSelection from '../../../hooks/useNavigationSelection';
import useNetwork from '../../../hooks/useNetwork';
import useReads from '../../../hooks/useReads';
import useRouteBoundedMachine from '../../../hooks/useRouteBoundedMachine';
import { requireReactNativeRuntime } from '../../../utilities/requireReactNativeRuntime';
import UnanchoredOverlay from '../../_abstractComponent/UnanchoredOverlay';
import MergedReadProvider, { useMergedReads } from '../../contextProviders/MergedReadProvider';
import SensorReadList from '../../list/SensorReadList';
import { ShowUnknownTagsLabel, UnknownTagSwitch } from '../../list/SensorReadList/styles';
import {
    getIdColumnLabel,
    getRequiredSelectionId,
    getTagPrefixForSession,
    resolveTenantTitleForContinuousScan
} from '../helpers';
import machineConfig, { actions as actionsFromConfig } from './markRecordStateMachine';
import {
    BoundingView,
    ButtonBar,
    CloseModalButton,
    CountRow,
    LittleDarker,
    MarkFoundButton,
    OfflineIcon,
    ShowUnknownTags,
    SpreadLeftToRight,
    Total,
    TotalLabel,
    TotalNumber,
    UserInput
} from './styles';

const EMPTY_ARRAY = [];

// Long enough that a slow-but-working credentials fetch is never mistaken for a hung one - this is
// only here to stop an unsettled promise wedging the in-flight guard, not to enforce a latency SLA.
const START_SESSION_CREDENTIALS_TIMEOUT_MS = 30000;

const requireOptionalReactNative = () => {
    // Only attempt to load react-native in actual React Native environments
    if (typeof __SST_REACT_NATIVE__ === 'undefined' || !__SST_REACT_NATIVE__) {
        throw new Error('react-native loader: not in React Native context');
    }
    return requireReactNativeRuntime();
};

const _p = {
    getLocationId: getLocation,
    getBuildingId: getBuilding,
    getCompanyId: getCompany,
    useDbView,
    useNetwork,
    useContinuousScanEnabled,
    MarkRecord,
    useMergedReads,
    useReads,
    useNavigationSelection,

    // require('react-native') genuinely throws outside a real RN bundle (confirmed in this
    // package's own Node test environment) - swappable so tests can substitute a fake
    // NativeModules instead of only ever exercising the catch-and-return-null path (mirrors
    // MultiPickerTransactionGrid/index.js's _p.requireReactNative).
    requireReactNative: () => requireOptionalReactNative(),

    // The one seam for the whole broker-connection block — credentials, normalized tenant, group and
    // URL. Swappable so tests can substitute a fake; shared with both MultiPickerTransactionGrid
    // variants so all three derive it identically (see getContinuousScanTransport).
    getContinuousScanTransport: (...args) => getContinuousScanTransport(...args)
};

export const _private = _p;
export default memo(MarkRecord);

/**
 * Find a record by one or more properties, E.g. on the Take screen. Uses rules for the "mark" verb for actual updating or creating of the record.
 * @param {Object} props
 * @param {string} props.currentRoute
 * @param {{[key: string]: string | boolean}} props.hNode
 */
function MarkRecord(props) {
    const {
        hNode: { namespace, relation, matchProperties = ['assetNo', 'serialNo'], id }
    } = props ?? { hNode: {} };

    const transactionProps = {
        errorNotificationDebounceMils: 400,
        hNode: {
            id: `MergedReadProvider${id}`,
            namespace,
            relation: `${relation}-transaction`,
            propertyName: 'includedItems',
            transactionType: 'markRecord',
            matchProperties,
            unknownTagAction: constants.tagAction.NONE,
            inactiveTagAction: constants.tagAction.REMOVE_AND_ERROR,
            beepForNewReads: false,
            publishChanges: false,
            duplicateTagAction: constants.duplicateTagAction.REMOVE_AND_ERROR
        }
    };

    return rc(MergedReadProvider, transactionProps, rc(InnerMarkRecord, props));
}

function InnerMarkRecord(props) {
    const {
        inputDebounce = 800,
        currentRoute,
        hNode,
        hNode: { forAction, id, namespace, relation }
    } = props || { hNode: {} };
    const { mobile, native } = useTheme();
    const { isOnline, statusResolved } = _p.useNetwork();
    const sessionContext = useContext(contexts.SessionContext);
    const foundBy = sessionContext.briefUserReference;
    const tenantId =
        sessionContext?.tenant?.['identity:tenant']?._id ??
        sessionContext?.tenant?._id ??
        sessionContext?.profile?.tenant?.[0]?.['identity:tenant']?._id ??
        sessionContext?.profile?.tenant?.[0]?._id;
    const tenantTitle = resolveTenantTitleForContinuousScan(sessionContext);
    const userId = sessionContext?.profile?.userId ?? sessionContext?.profile?._id ?? sessionContext?.user?._id;
    const userDisplayName =
        sessionContext?.profile?.displayName ??
        sessionContext?.profile?.name ??
        sessionContext?.user?.displayName ??
        'User';
    const activeUseCase = sessionContext?.useCase?.['metaui:useCase'];
    const useCaseId = activeUseCase?._id ?? 'physical-inventory';
    const useCaseTitle = activeUseCase?.title ?? 'Physical Inventory';
    const router = hooks.useRouter();

    const [offlineTimestamp, setOfflineTimestamp] = useState(null);
    useEffect(() => {
        if (statusResolved && !isOnline) {
            setOfflineTimestamp(prev => prev ?? new Date().toLocaleString());
        } else {
            setOfflineTimestamp(null);
        }
    }, [isOnline, statusResolved]);
    const offlineText = offlineTimestamp ? `Device Connection Lost: ${offlineTimestamp}` : undefined;
    const [subscribe, publish, request] = useEventSink();
    const [disableMarkText, setDisableMarkText] = useState();
    const [inputActive, setInputActive] = useState();
    /** @type {[string|null, (id: string|null) => void]} */
    const [continuousScanSessionId, setContinuousScanSessionId] = useState(/** @type {string|null} */ (null));
    const markRecordInputRef = useRef();
    const hasAutoNavigatedToContinuousScanRef = useRef(false);
    // Set right before navigating to ContinuousScanPage to hand off the active session, so the
    // abort-on-unmount effect below doesn't cancel the very session it just handed off - MarkRecord
    // unmounts as part of that route change same as it would on a genuine abandon.
    const handingOffSessionRef = useRef(false);

    // Get NativeModules safely (only available in React Native)
    const getNativeModule = useCallback(() => {
        try {
            if (typeof __SST_REACT_NATIVE__ !== 'undefined' && __SST_REACT_NATIVE__) {
                const { NativeModules } = _p.requireReactNative();
                return NativeModules;
            }
        } catch {
            logging.warn('[ContinuousScan] NativeModules not available');
        }
        return null;
    }, []);
    const titleAlternative = metadata.getTitleAlternative(namespace, relation, 'assetNo');
    const titleLabel = metadata.getTitleAlternativePretty(namespace, relation, 'Asset ID');

    // Database stuff
    const { viewCriteria } = _p.useDbView(namespace, relation, undefined, hNode, undefined, false);
    const locationId = _p.getLocationId(viewCriteria);
    // Building/company tiers of the same 3-tier location picker - included in SessionStart's
    // formMetadata alongside location:location so the server records the full hierarchy, not
    // just the leaf. Either may be absent if the picker wasn't configured with those tiers.
    const buildingId = _p.getBuildingId(viewCriteria);
    const companyId = _p.getCompanyId(viewCriteria);
    /**
     * @type { {namespace:string, relation:string, record: object, available:boolean, loading:boolean }}} NavigationSelection
     */
    const navigationSelection = _p.useNavigationSelection();
    // The active Inventory (physical count) record - included in SessionStart's formMetadata as
    // inventory:inventory so silo/stream can scope per-inventory found tracking and build the
    // item's inventory[] earmark entry against the right inventory. Was already available here
    // for the manual mark-as-found dispatch, just never threaded into the continuous-scan session.
    const inventoryId = navigationSelection?.record?._id;
    const inventoryTitle = navigationSelection?.record?.title;

    // RFID is considered "checked" when scanRfid is enabled for this hNode (defaults to true, see userActivatedInputHNode below).
    const rfidChecked = hNode.scanRfid !== false;
    // Continuous scan's PRD scope (docs/product/prds/continuous-scan/continuous-scan.md §3) is
    // "physical inventory and mass update" only - MarkRecord itself is also reused for Check
    // In/Check Out screens (same component, different hNode config), which must NOT get
    // continuous scan just because the tenant-wide setting is on. hNode.title is a per-field
    // label (e.g. "Scan Asset ID or Serial #"), not the screen identity, so it can't be used to
    // tell screens apart - the route path can: LayoutTitle.js derives the app-bar title the same
    // way (last path segment of the route), confirmed on-device as '/g/0/Inventory/Count'.
    const currentScreenName = currentRoute?.split('/').filter(Boolean).pop();
    const isContinuousScanScreen = currentScreenName === 'Count';
    // Reactive (see useContinuousScanEnabled): globalConfig().tenantSettings is still undefined
    // for the first seconds after a cold start, so reading the snapshot here left this screen on
    // the legacy path until something unrelated forced a re-render.
    const continuousScanTenantSetting = _p.useContinuousScanEnabled();
    const continuousScanAvailable =
        native && mobile && isContinuousScanScreen && continuousScanTenantSetting && locationId;

    // Continuous scan owns the RFID trigger on this screen, but the legacy JS read path keeps
    // firing alongside it - see the hook for why. Without this, reader reads land in the
    // SensorReadList below and inflate the Count, including in the ~1-3 s window between the
    // trigger pull and the native session actually existing (#1817).
    useBlockReaderReadsOnSetupScreen(!!continuousScanAvailable, subscribe, publish);

    // Titles for the location/building/company tiers - viewCriteria only carries the selected
    // _ids, so these are looked up once per id change and sent alongside SessionStart's
    // formMetadata (server payload needs both _id and title per tier, not just _id).
    const [tierTitles, setTierTitles] = useState({ locationTitle: null, buildingTitle: null, companyTitle: null });
    const [tierTitlesLoaded, setTierTitlesLoaded] = useState(false);
    useEffect(() => {
        // Tier titles are consumed ONLY by the continuous-scan SessionStart effect below, so don't
        // pay for the lookup - or its mid-interaction re-render - on every other MarkRecord screen.
        if (!continuousScanAvailable) return undefined;
        let cancelled = false;
        setTierTitlesLoaded(false);
        const lookups = [
            locationId &&
                request(
                    { _id: locationId },
                    { verb: 'get', namespace: 'location', relation: 'location', type: 'find' }
                ).then(r => ['locationTitle', r?.result?.[0]?.title ?? null]),
            buildingId &&
                request(
                    { _id: buildingId },
                    { verb: 'get', namespace: 'location', relation: 'building', type: 'find' }
                ).then(r => ['buildingTitle', r?.result?.[0]?.title ?? null]),
            companyId &&
                request(
                    { _id: companyId },
                    { verb: 'get', namespace: 'location', relation: 'company', type: 'find' }
                ).then(r => ['companyTitle', r?.result?.[0]?.title ?? null])
        ].filter(Boolean);

        Promise.all(lookups)
            .then(entries => {
                if (cancelled) return;
                setTierTitles({
                    locationTitle: null,
                    buildingTitle: null,
                    companyTitle: null,
                    ...Object.fromEntries(entries)
                });
                setTierTitlesLoaded(true);
            })
            .catch(err => {
                if (cancelled) return;
                logging.error('[ContinuousScan] Failed to look up location tier titles:', err);
                // Don't block SessionStart forever on a title-lookup failure - proceed without titles.
                setTierTitlesLoaded(true);
            });

        return () => {
            cancelled = true;
        };
    }, [continuousScanAvailable, locationId, buildingId, companyId, request]);

    // True from the moment startSession is called until it settles. Native installs all of a
    // session's state into instance fields, so a second startSession entered before the first
    // session's MQTT handshake completes leaves the first session's asynchronous continuations
    // acting on the second (#1689). Native now tears the first session down rather than letting it
    // corrupt the second, but the handshake is ~900ms on a TC22 and the dep array below is large
    // enough to re-fire well inside that - so don't make the second call at all. Mirrors
    // MultiPickerTransactionGrid's startingSessionRef.
    const startingSessionRef = useRef(false);
    // Bumped when a start that was already in flight is superseded by a dep change. The ref above
    // makes the superseding effect run bail out, and changing a ref does not re-render - so on its
    // own the guard would leave the screen with no session at all: the abandoned start cancels its
    // own session below, and nothing would ever start its replacement. This is state rather than a
    // ref precisely so that settling an abandoned start re-runs the effect exactly once.
    const [startSessionAttempt, setStartSessionAttempt] = useState(0);
    // Distinguishes the two things that set `cancelled` below. The retry only makes sense for a
    // dependency change, which leaves this component mounted and wanting a session; on unmount
    // there is nothing to start a session for, and the in-flight start is cancelled by the
    // `markrecord-start-cancelled` branch instead. Mount-scoped so only a real unmount sets it, and
    // reset on the way in, because refs survive the unmount/remount of StrictMode and Fast Refresh.
    const startSessionUnmountedRef = useRef(false);
    useEffect(() => {
        startSessionUnmountedRef.current = false;
        return () => {
            startSessionUnmountedRef.current = true;
        };
    }, []);

    // Send initiation message to native layer when RFID is checked and continuous scan is enabled
    useEffect(() => {
        if (
            native &&
            mobile &&
            rfidChecked &&
            isContinuousScanScreen &&
            continuousScanTenantSetting &&
            locationId &&
            tenantId &&
            userId &&
            tierTitlesLoaded
        ) {
            if (startingSessionRef.current) return undefined;
            const NativeModules = getNativeModule();
            if (NativeModules?.ContinuousScanModule?.startSession) {
                const tagPrefixPromise = getTagPrefixForSession(request);

                // Broker URL, release group and credentials all come from one place — including the
                // normalized tenantId the native client builds its topics from. See
                // getContinuousScanTransport for why none of it is assembled here.
                let cancelled = false;
                startingSessionRef.current = true;
                // The guard above is only released in .finally(), so a chain that never settles
                // wedges it for the life of the mount and no session ever starts on this screen
                // again. It cannot instead be released from the effect cleanup - releasing it early
                // is what would let a second startSession overlap the first - so the network call is
                // bounded instead.
                let credentialsTimeout;
                const credentials = new Promise((resolve, reject) => {
                    credentialsTimeout = setTimeout(
                        () =>
                            reject(
                                new Error(
                                    `Timed out after ${START_SESSION_CREDENTIALS_TIMEOUT_MS}ms waiting for MQTT credentials`
                                )
                            ),
                        START_SESSION_CREDENTIALS_TIMEOUT_MS
                    );
                    _p.getContinuousScanTransport({ tenantId }).then(resolve, reject);
                });
                Promise.all([credentials, tagPrefixPromise])
                    .then(([transport, tagPrefix]) => {
                        if (cancelled) return undefined;
                        const sessionConfig = {
                            ...transport,
                            tenantTitle,
                            userId,
                            locationId,
                            buildingId,
                            companyId,
                            inventoryId,
                            inventoryTitle,
                            locationTitle: tierTitles.locationTitle,
                            buildingTitle: tierTitles.buildingTitle,
                            companyTitle: tierTitles.companyTitle,
                            userDisplayName,
                            useCaseId,
                            useCaseTitle,
                            // MarkRecord is the Physical Inventory RFID trigger; explicit here so it
                            // doesn't rely on ContinuousScanModule's default (see classifyReads.ts -
                            // massUpdate has no "wrong location" concept, matched items are always
                            // 'found' rather than 'moved').
                            streamType: 'inventory',
                            tagPrefix
                        };
                        return continuousScanBridge.startContinuousScan(sessionConfig);
                    })
                    .then(
                        /** @param {{sessionId?: string}} [result] */ result => {
                            const sessionId = result?.sessionId ?? null;
                            if (!cancelled) {
                                setContinuousScanSessionId(sessionId);
                                return;
                            }
                            // This effect's cleanup can run while startSession is still in flight -
                            // continuousScanTenantSetting flipping true -> false mid-start is enough.
                            // By the time it resolves the native session is live, but nothing has
                            // stored its id, and both cancel effects below are keyed on
                            // continuousScanSessionId - so neither would ever fire and the MQTT
                            // connection plus RFID continuous mode would leak for the life of the
                            // process. Abort it here rather than dropping the id on the floor.
                            if (sessionId && !handingOffSessionRef.current) {
                                continuousScanBridge
                                    .cancelContinuousScan({ sessionId, reason: 'markrecord-start-cancelled' })
                                    .catch(err => {
                                        logging.warn('[ContinuousScan] Error aborting orphaned session:', err);
                                    });
                            }
                        }
                    )
                    .catch(
                        /** @param {Error} err */ err => {
                            logging.error('[ContinuousScan] Failed to start session:', err);
                        }
                    )
                    .finally(() => {
                        clearTimeout(credentialsTimeout);
                        startingSessionRef.current = false;
                        // Superseded while in flight: the branch above has just cancelled this
                        // run's session, and the effect run that superseded it bailed out on the
                        // guard, so nothing else will start a replacement. Re-run once now that
                        // the overlap this guard exists to prevent is over.
                        //
                        // Not on unmount, which sets `cancelled` too. Nothing is left to start a
                        // session for there, and the bump would be a state update on a component
                        // that no longer exists - a silent no-op under React 19, but one that reads
                        // as if a retry were expected when none is.
                        if (cancelled && !startSessionUnmountedRef.current) {
                            setStartSessionAttempt(attempt => attempt + 1);
                        }
                    });

                return () => {
                    cancelled = true;
                };
            }
        }
    }, [
        native,
        mobile,
        isContinuousScanScreen,
        continuousScanTenantSetting,
        rfidChecked,
        locationId,
        buildingId,
        companyId,
        inventoryId,
        inventoryTitle,
        tierTitles,
        tierTitlesLoaded,
        tenantId,
        tenantTitle,
        userId,
        useCaseId,
        useCaseTitle,
        getNativeModule,
        request,
        startSessionAttempt
    ]);

    // Send abort message to native layer when component unloads or continuous scan is disabled,
    // so the MQTT connection is closed and RFID goes back to normal mode. Routed through
    // continuousScanBridge (rather than calling NativeModules directly) so this shares the
    // same web-safe module lookup and error logging as ContinuousScanPage's own abort call.
    useEffect(() => {
        return () => {
            if (native && continuousScanSessionId && !handingOffSessionRef.current) {
                continuousScanBridge
                    .cancelContinuousScan({ sessionId: continuousScanSessionId, reason: 'markrecord-unmounted' })
                    .catch(err => {
                        logging.warn('[ContinuousScan] Error aborting session:', err);
                    });
            }
        };
    }, [native, continuousScanSessionId]);

    // Auto-cancel a session that auto-started (rfidChecked while this was the Count screen, see
    // the startSession effect above) but was never handed off to the actual scan UI, if the user
    // navigates away from the Count screen without ever pulling the trigger. Without this,
    // MarkRecord doesn't necessarily unmount just because currentScreenName changes away from
    // 'Count' (it can stay mounted across sibling screens), so the unmount-cleanup effect above
    // would never fire, native's session would stay active indefinitely, and the next time the
    // user returns to Count and pulls the trigger, startSession would silently resolve with that
    // same stale sessionId instead of starting fresh (see PR review / bug report).
    const wasContinuousScanScreenRef = useRef(isContinuousScanScreen);
    useEffect(() => {
        if (
            wasContinuousScanScreenRef.current &&
            !isContinuousScanScreen &&
            native &&
            continuousScanSessionId &&
            !handingOffSessionRef.current
        ) {
            continuousScanBridge
                .cancelContinuousScan({ sessionId: continuousScanSessionId, reason: 'markrecord-navigated-away' })
                .catch(err => {
                    logging.warn('[ContinuousScan] Error auto-cancelling session on navigation away:', err);
                });
            setContinuousScanSessionId(null);
        }
        wasContinuousScanScreenRef.current = isContinuousScanScreen;
    }, [isContinuousScanScreen, native, continuousScanSessionId]);

    // TODO: Blockly allows forAction to be something different, but it doesn't make
    // sense at this point because the corresponding rules engine rules
    // expect 'mark' for the verb.
    // It's possible this block could be used for some other action, but
    // until it is needed it, this safeguard may prevent some heartache.
    if (forAction !== 'mark') {
        throw new Error(
            `For now the "forAction" value for the MarkRecord block need to be "mark" not ${forAction}.  If another verb is needed, this rules engine rules will need to be built out.`
        );
    }

    // Read state stuff
    const { reads: allReads } = _p.useReads();
    const [records, setRecords] = useState(EMPTY_ARRAY);

    const { subscribeToChange, includeUnknownTags, toggleIncludeUnknownTags } = _p.useMergedReads();
    useEffect(() => {
        if (subscribeToChange != null) {
            return subscribeToChange(newRecords => {
                setRecords(newRecords);
            });
        }
    }, [subscribeToChange]);
    const readsExist = (allReads?.get?.()?.length ?? 0) > 0;
    const filteredReadsExist = (records?.length ?? 0) > 0;

    // SERVICES for the markRecordStateMachine
    const service_resetReads = useCallback(() => {
        return new Promise(resolve => {
            request(
                { sensorType: constants.sensorTypes.RFID },
                { verb: 'reset', namespace: 'sensor', relation: 'read', type: 'readProvider' } // type: 'readProvider' ensures readProvider's reads are reset as well
            ).then(() => {
                // Wait until next cycle or the reads will still be present in the machine and the
                // modal will open again.
                setTimeout(resolve, 1);
            });
        });
    }, [request]);

    // ACTIONS for the markRecordStateMachine
    const action_logError = useCallback((context, event) => {
        logging.error(event.data);
    }, []);

    const action_applyFilter = useDebounce(
        /**
         * @parm {string} markText
         */
        function (context) {
            viewCriteria.applyFilters(
                {
                    // paged filter is a noop for a local lookup, but is needed for an online lookup.
                    paged: { page: 0 },
                    fullTextSearch: {
                        searchTerm: context.markText
                    },
                    propertiesToSearch: {
                        propertiesToSearch: hNode.children?.find(c => c.hNodeType === 'PropertiesToSearch')
                            ?.propertiesToSearch || ['assetNo', 'serialNo']
                    }
                },
                // Use a transient id to avoid overwriting the metadata based filters
                `transient_${id}`
            );
        },
        [viewCriteria, id, hNode],
        inputDebounce || 800,
        {
            leading: false,
            trailing: true
        }
    );

    /**
     * for each record in the list, set inventory found = true and save
     */
    const service_markAllAsFound = useCallback(() => {
        // Cancel any pending debounced applyFilter requests (they serve no purpose
        // because we are going to clear the filter).
        action_applyFilter.cancel();
        return request(
            {
                records,
                includeUnknownTags,
                locationId,
                foundBy,
                navigationSelection
            },
            { verb: forAction, namespace, relation, type: 'all' },
            constants.MARKALL_TIMEOUT
        );
    }, [
        request,
        action_applyFilter,
        records,
        includeUnknownTags,
        locationId,
        foundBy,
        forAction,
        namespace,
        relation,
        navigationSelection
    ]);

    const action_clearMarkTextFilter = useCallback(() => {
        // Immediately clear the filter.  This prevents hard to debug race
        // conditions with the handleMarkTextChange above.
        viewCriteria.applyFilters(
            {
                fullTextSearch: {
                    searchTerm: ''
                },
                propertiesToSearch: {
                    propertiesToSearch: hNode.children?.find(c => c.hNodeType === 'PropertiesToSearch')
                        ?.propertiesToSearch || ['assetNo', 'serialNo']
                }
            },
            // Use a transient id to avoid overwriting the metadata based filters
            `transient_${id}`
        );
    }, [id, viewCriteria, hNode]);

    /**
     * Publishes the action specified in the forAction field of the metadata.
     * For now, this needs to be 'mark'.
     */
    const action_markAsFound = useCallback(
        (context, event) => {
            // Don't mark an empty serialNo or assetNo
            if (!event.markText || locationId == null) {
                logging.error('Mark as found was called without an assetNo or without a location.');
                return;
            }

            // Cancel any pending debounced applyFilter requests (they serve no purpose
            // because we are going to clear the filter).
            action_applyFilter.cancel();

            // publish our action - probably a 'mark' verb.
            publish(
                {
                    markText: event.markText,
                    sensorType: event.sensorType,
                    locationId,
                    foundBy,
                    caseSensitive: false,
                    exactMatchOnly: true,
                    navigationSelection
                },
                { verb: forAction, namespace, relation, routePath: currentRoute }
            );
        },
        [
            forAction,
            action_applyFilter,
            foundBy,
            currentRoute,
            locationId,
            namespace,
            relation,
            publish,
            navigationSelection
        ]
    );

    const action_displayHelpText = useDebounce(
        () => {
            publish(
                {
                    message: 'Please select a room to enable RFID scanning.',
                    type: constants.notificationTypes.TOAST,
                    variant: 'info'
                },
                {
                    verb: 'pop',
                    namespace: 'application',
                    relation: 'notification'
                }
            );
            setTimeout(() => {
                publish(
                    { sensorType: constants.sensorTypes.RFID },
                    { verb: 'reset', namespace: 'sensor', relation: 'read', type: 'readProvider' } // type: 'readProvider' ensures readProvider's reads are reset as well
                );
            }, 1);
        },
        [publish],
        1000,
        { leading: true, trailing: false }
    );

    const [routeState, send] = useRouteBoundedMachine(
        { ...machineConfig, context: { native, request, mobile, markText: '' } },
        {
            markAsFound: action_markAsFound,
            clearMarkTextFilter: action_clearMarkTextFilter,
            applyFilter: action_applyFilter,
            logError: action_logError,
            displayHelpText: action_displayHelpText,
            ...actionsFromConfig
        },
        {
            resetReads: service_resetReads,
            markAllAsFound: service_markAllAsFound
        }
    );

    useEffect(() => {
        send('readCountChange', { readsExist, filteredReadsExist });
    }, [readsExist, filteredReadsExist, send]);

    // Machine derived state
    const machineStarted = routeState.machine != null;
    const modalOpen = routeState.matches('modal');
    const detailOpen = routeState.matches('detail');
    const currentMarkText = routeState.context?.markText;

    function openContinuousScanRoute() {
        // Pass along the session already initiated when RFID was checked, so Screen B
        // doesn't open a second, redundant MQTT session.
        handingOffSessionRef.current = true;
        // useNavigation.js blocks every history.push and replays it through the rules engine's
        // navigate_application_route workflow, which only carries the path/search through
        // (goToLocation calls history.push(newLocation) with no second argument) - any state
        // passed as history.push(path, state) is silently dropped. Query params survive that
        // replay, so scan params are passed there instead of as router state.
        const query = queryString.stringify({
            title: 'Count',
            streamType: 'inventory',
            sessionId: continuousScanSessionId ?? '',
            locationId: locationId ?? '',
            // Screen D has no hNode to resolve this from - see getIdColumnLabel. This screen's own
            // columns are built from the same static map below (titleLabel), and its hNode has no
            // matching listColumn child, so this resolves to the identical 'Asset ID' the
            // SensorReadList above already shows - the two stay in agreement, as they do today.
            idLabel: getIdColumnLabel(hNode, namespace, relation)
        });
        router.history.push(`/continuous-scan?${query}`);
    }

    // Auto-navigate to the Active Count screen (Screen B) as soon as the RFID trigger is
    // pulled on this setup screen, once company/building/room are all selected - no tap into
    // the mark-text input or a Start button required. ContinuousScanModule.onTriggerPressed
    // emits continuousScanResumed on every trigger press unconditionally, regardless of
    // whether a backend scan session is active, specifically so this listener can catch it.
    useEffect(() => {
        if (!native || !mobile || !continuousScanAvailable) return undefined;
        const NativeModules = getNativeModule();
        const ContinuousScanModule = NativeModules?.ContinuousScanModule;
        if (!ContinuousScanModule) return undefined;

        let NativeEventEmitter;
        try {
            ({ NativeEventEmitter } = _p.requireReactNative());
        } catch {
            return undefined;
        }

        const emitter = new NativeEventEmitter(ContinuousScanModule);
        const subscription = emitter.addListener('continuousScanResumed', () => {
            if (!hasAutoNavigatedToContinuousScanRef.current) {
                hasAutoNavigatedToContinuousScanRef.current = true;
                openContinuousScanRoute();
            }
        });

        // Reset session state once the session ends (submit/finish/cancel/error) - otherwise
        // continuousScanSessionId keeps pointing at a session the server has already torn down,
        // and hasAutoNavigatedToContinuousScanRef stays permanently tripped so the next trigger
        // pull silently re-opens the dead session instead of starting a fresh one.
        const sessionEventSubscription = emitter.addListener('continuousScanSessionEvent', event => {
            if (['submitted', 'finished', 'cancelled', 'error'].includes(event?.type)) {
                setContinuousScanSessionId(null);
                hasAutoNavigatedToContinuousScanRef.current = false;
            }
        });

        return () => {
            subscription.remove();
            sessionEventSubscription.remove();
        };
    }, [native, mobile, continuousScanAvailable, getNativeModule]);

    // Machine Events
    // Focusing (or hovering) the mark-text input must NOT hand off to Screen B. It used to, which
    // meant the field could never be typed into: tapping it navigated away before a character
    // landed. The trigger pull and the RFID button are the two deliberate ways into continuous
    // scan (see the continuousScanResumed listener above and onStartContinuousScan below); tapping
    // the input is how a user enters an Asset ID by hand, which is the whole point of the field
    // (#1658 item 5, #1817). inputHovered is gone entirely for the same reason - its only job was
    // that hand-off.
    const inputFocused = useCallback(
        e => {
            setInputActive(true);
            send('inputFocused', {
                blur: () => {
                    e.target.blur();
                }
            });
        },
        [send]
    );
    const inputBlurred = useCallback(() => {
        setInputActive(false);
        send('inputBlurred');
    }, [send]);
    const onMarkAsFound = useCallback(
        (markText, sensorType) => {
            send('markAsFound', { markText, sensorType });
        },
        [send]
    );
    const onMarkTextChanged = useCallback(
        (markText, sensorType) => send('markTextChanged', { markText, sensorType }),
        [send]
    );
    const onLocationChange = useCallback(
        locationId => {
            if (locationId) {
                send('locationChange', { locationId });
            } else {
                send('locationCleared', { locationId });
            }
        },
        [send]
    );
    const onMarkAllSuccess = useCallback(() => send('markAllSuccess'), [send]);
    const onCloseModal = useCallback(
        e => {
            e.stopPropagation();
            send('closeModal');
        },
        [send]
    );
    const onMarkAllAsFound = useCallback(() => send('markAllAsFound'), [send]);
    const onStartContinuousScan = useCallback(() => {
        openContinuousScanRoute();
    }, [continuousScanAvailable, continuousScanSessionId, locationId, router]);
    const onChangeAbandonConfirmationFailed = useCallback(() => send('changeAbandonConfirmationFailed'), [send]);
    useEffect(() => {
        return subscribe(
            {
                verb: 'confirm',
                namespace: 'item',
                relation: 'item',
                type: 'changeRecord',
                status: 'failure'
            },
            () => {
                onChangeAbandonConfirmationFailed();
            }
        );
    }, [subscribe, onChangeAbandonConfirmationFailed]);

    const onMarkAsFoundFailed = useCallback(() => send('reset'), [send]);
    useEffect(() => {
        return subscribe(
            {
                verb: 'mark',
                namespace: 'item',
                relation: 'item',
                status: 'failure'
            },
            () => {
                onMarkAsFoundFailed();
            }
        );
    }, [subscribe, onMarkAsFoundFailed]);
    // Let the state machine know when an unknown tag is clicked.
    const onUnknownItemClicked = useCallback(() => send('unknownTagClicked'), [send]);
    useEffect(() => {
        return subscribe({ verb: 'new', namespace, relation }, payload => {
            if (
                payload?.newRecord?.assetNo?.startsWith(`<${constants.UNKNOWN_TAG}`) ||
                payload?.newRecord?.assetNo?.startsWith(`<${constants.NEW_TAG}`)
            ) {
                onUnknownItemClicked();
            }
        });
    }, [subscribe, namespace, relation, onUnknownItemClicked]);

    // Let the state machine know when an known tag is clicked.
    const onKnownItemClicked = useCallback(() => send('knownTagClicked'), [send]);
    useEffect(() => {
        return subscribe({ verb: 'edit', namespace, relation }, () => {
            onKnownItemClicked();
        });
    }, [subscribe, namespace, relation, onKnownItemClicked]);

    // If a new unknown tag is created, add the locationId to it.  This works because data
    // is a reference.
    useEffect(() => {
        subscribe({ verb: 'new', namespace, relation, priority: 10 }, data => {
            data.locationId = locationId;
        });
    }, [subscribe, locationId, namespace, relation]);

    // If the location changes let the machine know.
    useEffect(() => onLocationChange(locationId), [locationId, onLocationChange]);

    const [delayedFocus, setDelayedFocus] = useState(false);
    /**
     * if location does not exist, then disable text input.
     * Also, if conditions are right, auto focus on the text input.
     */
    useEffect(() => {
        setDisableMarkText(locationId == null || detailOpen);
        if (locationId && !detailOpen) {
            setDelayedFocus(true);
        }
    }, [locationId, mobile, detailOpen, native]);

    useEffect(() => {
        if (!disableMarkText && delayedFocus) {
            // Native has a problem where it can be focused before enabled,
            // then additional focus calls will not add the focus unless the
            // input is blurred.
            // Only Android has the `isFocused` method.
            if (markRecordInputRef.current?.isFocused?.()) {
                markRecordInputRef.current?.blur();
            } else if (!native) {
                // Native MarkRecord input should not be automatically focused
                // because it opens the modal view.
                markRecordInputRef.current?.focus();
            }
            setDelayedFocus(false);
        }
    }, [disableMarkText, delayedFocus, native]);

    // If a mark all found completes successfully, then reset reads and close
    // modal screen
    useEffect(() => {
        const unsubscribes = [
            subscribe({ verb: forAction, namespace, relation, type: 'all', status: 'success' }, () => {
                onMarkAllSuccess();
            })
        ];
        return () => unsubscribes.forEach(unsubscribe => unsubscribe());
    }, [forAction, namespace, onMarkAllSuccess, relation, subscribe]);

    useEffect(() => {
        // when unit testing, we rely on the ability to "stage" the environment

        if (!__UNIT_TESTING__) {
            //we need to make sure we start "fresh". Just in case they navigated away while on detail on modal.
            send('reset', {});
        }
    }, [send]);

    // Default all scanning properties to true (which will prefer the RFID Power Button if possible).
    const userActivatedInputHNode = useMemo(
        () => ({ scanRfid: true, scanBarcode: true, displayScanButton: true, displayRfidPowerButton: true, ...hNode }),
        [hNode]
    );
    if (!machineStarted) return rc(Text, {}, 'Loading...');

    // NORMAL MODE: Show existing modal
    if (native && mobile && modalOpen) {
        // prettier-ignore
        return rc(Fragment, null,
            rc(UserInput, {
                id: `normalMarkRecord-${id}`,
                disabled: disableMarkText,
                currentRoute,
                hNode: userActivatedInputHNode,
                currentValue: currentMarkText,
                onSubmit: onMarkAsFound,
                onChange: onMarkTextChanged,
                onRfidButtonClick: continuousScanAvailable ? onStartContinuousScan : undefined,
                forceShowRfidButton: !!continuousScanAvailable,
                inputRef: markRecordInputRef,
                inputFocused,
                inputBlurred
            }),
            rc(Modal, { visible: true, id: `modalMarkRecord-${id}` },
                rc(BoundingView, null,
                    rc(CloseModalButton, {
                        title: 'Close',
                        alt: 'Close',
                        onClick: onCloseModal,
                        icon: 'close',
                        buttonStyle: 'Dark',
                        ...testProperties(hNode, 'modalClose')
                    }),
                    rc(LittleDarker, null,
                        rc(SpreadLeftToRight, null,
                            rc(UserInput, {
                                id: `modalMarkRecord-${id}`,
                                disabled: disableMarkText,
                                currentRoute,
                                hNode: userActivatedInputHNode,
                                currentValue: currentMarkText,
                                onSubmit: onMarkAsFound,
                                onChange: onMarkTextChanged,
                                onRfidButtonClick: continuousScanAvailable ? onStartContinuousScan : undefined,
                                forceShowRfidButton: !!continuousScanAvailable,
                                inputRef: markRecordInputRef,
                                readsExist,
                                autoFocus: true
                            }),
                            rc(Total, null,
                                rc(TotalLabel, null, 'Count'),
                                rc(CountRow, null,
                                    offlineText && rc(OfflineIcon),
                                    rc(TotalNumber, null, `${records.length || 0}`)
                                )
                            )
                        ),
                        rc(SensorReadList, {
                            currentRoute,
                            hNode: {
                                includeReadButton: false,
                                displayShowUnknownTags: false,
                                id: `sensor-list-${id}`,
                                mergeNamespace: namespace,
                                mergeRelation: relation,
                                sensorType: 'RFID',
                                hNodeType: 'SensorReadList',
                                hNodeTypeGroup: 'list',
                                offlineText,
                                children: [
                                    {
                                        id: `remove${id}`,
                                        hNodeType: 'button',
                                        hNodeTypeGroup: 'listColumn',
                                        dataType: 'button',
                                        title: 'Remove',
                                        label: 'Remove',
                                        namespace: 'sensor',
                                        relation: 'read',
                                        iconName: 'clear',
                                        forAction: 'remove',
                                        displayTitleOnButton: false,
                                        buttonStyle: 'round'
                                    },
                                    {
                                        'id': 'bb6116dac0fefb2133bccd17a0',
                                        'hNodeType': 'Text',
                                        'hNodeTypeGroup': 'listColumn',
                                        'title': titleLabel,
                                        'label': titleLabel,
                                        'propertyName': titleAlternative,
                                        'dataType': 'text',
                                        'sequence': 0,
                                        'sortable': 'false',
                                        'visuallyFlagOnValue': ''
                                    },
                                    {
                                        'id': 'bb6116db4afefb2133bccd17ae',
                                        'hNodeType': 'Text',
                                        'hNodeTypeGroup': 'listColumn',
                                        'title': 'Description',
                                        'label': 'Description',
                                        'propertyName': 'description',
                                        'dataType': 'text',
                                        'sequence': 1,
                                        'sortable': 'false',
                                        'visuallyFlagOnValue': ''
                                    }
                                ]
                            }
                        })
                    ),
                    rc(ButtonBar, null,
                        rc(ShowUnknownTags, null,
                            rc(UnknownTagSwitch, { onClick: toggleIncludeUnknownTags, value: includeUnknownTags }),
                            rc(ShowUnknownTagsLabel, null, 'Show Unknown Tags')
                        ),
                        rc(MarkFoundButton, { value: 'Mark Found', onClick: onMarkAllAsFound, disabled: records.length < 1 })
                    )
                )
            )
        );
    }

    // Using CSS to vary the appearance (based on keyboard presence and focus)
    // instead of rendering different components, avoids problems with focus
    // due to the input disappearing and reappearing.
    return rc(
        UnanchoredOverlay,
        { active: inputActive },
        rc(UserInput, {
            id: `normal-${id}`,
            disabled: disableMarkText,
            currentRoute,
            hNode: userActivatedInputHNode,
            currentValue: currentMarkText,
            onSubmit: onMarkAsFound,
            onChange: onMarkTextChanged,
            onRfidButtonClick: continuousScanAvailable ? onStartContinuousScan : undefined,
            forceShowRfidButton: !!continuousScanAvailable,
            inputRef: markRecordInputRef,
            inputFocused,
            inputBlurred
        })
    );
}

MarkRecord.propTypes = {
    currentRoute: PropTypes.string,
    hNode: PropTypes.shape({
        treePosition: PropTypes.shape({
            sequence: PropTypes.number.isRequired
        }).isRequired
    }).isRequired
};

function getLocation(viewCriteria) {
    return getRequiredSelectionId(viewCriteria, 'location', 'location');
}

function getBuilding(viewCriteria) {
    return getRequiredSelectionId(viewCriteria, 'location', 'building');
}

function getCompany(viewCriteria) {
    return getRequiredSelectionId(viewCriteria, 'location', 'company');
}
